import { Activity } from 'lucide-react';

/**
 * Tiny pure-SVG sparkline. Built inline (no Recharts overhead) because
 * the bars are 30 px wide and we may render dozens per table row.
 * Hovering a bar shows its date + count via the SVG `title` element so
 * users can tell exactly when an event happened without leaving the
 * table.
 *
 * Props:
 *   series — array of {date: string, count: number}, oldest first
 *   max    — peak count (so all bars share a y-axis), defaults to series max
 *   total  — total count across the series, rendered as trailing text
 *   tone   — 'violet' (default) | 'sky' | 'emerald' for the active bars
 *   width  — total svg width in pixels (default 90)
 *   height — total svg height in pixels (default 22)
 *   icon   — set to false to hide the leading Activity icon
 *
 * Originally lived inline at the bottom of BankingRulesPage.jsx and was
 * extracted here in iter351 so menu-items / suppliers / customers can
 * reuse the same visual without copy-paste.
 */
export function Sparkline({
  series = [],
  max,
  total,
  tone = 'violet',
  width = 90,
  height = 22,
  icon = true,
}) {
  const n = series.length;
  const gap = 1;
  const barW = (width - gap * (n - 1)) / Math.max(n, 1);
  const peak = Math.max(max ?? Math.max(...series.map((d) => d.count || 0), 0), 1);
  const sum = total ?? series.reduce((a, b) => a + (b.count || 0), 0);

  const toneMap = {
    violet: { icon: 'text-violet-600', bar: 'fill-violet-500' },
    sky: { icon: 'text-sky-600', bar: 'fill-sky-500' },
    emerald: { icon: 'text-emerald-600', bar: 'fill-emerald-500' },
    rose: { icon: 'text-rose-600', bar: 'fill-rose-500' },
  };
  const t = toneMap[tone] || toneMap.violet;

  return (
    <span className="inline-flex items-center gap-1.5" title={`${sum} events in last ${n} days`}>
      {icon ? <Activity size={11} className={`${t.icon} shrink-0`} /> : null}
      <svg width={width} height={height} className="overflow-visible">
        {series.map((d, i) => {
          const barH = d.count === 0 ? 1 : Math.max(2, (d.count / peak) * (height - 2));
          const x = i * (barW + gap);
          const y = height - barH;
          return (
            <rect
              key={d.date || i}
              x={x}
              y={y}
              width={Math.max(barW, 1)}
              height={barH}
              rx={1}
              className={d.count > 0 ? t.bar : 'fill-stone-200 dark:fill-stone-700'}
            >
              <title>{`${d.date}: ${d.count} ${d.count === 1 ? 'event' : 'events'}`}</title>
            </rect>
          );
        })}
      </svg>
      <span className="text-[10px] tabular-nums text-muted-foreground">{sum}</span>
    </span>
  );
}
